/**
 * New Tab page — the Chrome-style landing shown when an agent opens a new
 * tab from the "+" button in AgentTabs.
 *
 * Laid out as a command palette rather than a tile grid: one search box with a
 * single vertical list of rows underneath, so the same ↑/↓ + Enter affordance
 * drives both states.
 *
 * Two things live here:
 *   1. A single Google-like global search box. As the agent types (>= 2
 *      chars, debounced) it calls the shared /search endpoint and surfaces
 *      matching *interactions* (conversations, messages, contacts, calls,
 *      agents) that can be opened directly — conversations/messages deep-link
 *      into the unified inbox via ?conversationId=.
 *   2. Page shortcuts ("pages to open") as grouped rows. With an empty query
 *      the curated `primary` pages show under their section headings; while
 *      searching, every page whose label matches is listed under "Pages",
 *      above the interaction results.
 *
 * Rows carry live badges where a cheap count exists (see useLauncherCounts):
 * unread inbox, pending omnichannel queue, active live chats.
 *
 * Keyboard: ↑/↓ move the active row across the combined (pages + interactions)
 * list, Enter opens it. This replaces the old "+" dropdown entirely.
 */
import { useState, useMemo, useRef, useEffect, useCallback, type JSX } from 'react'
import { useNavigate } from 'react-router'
import {
  Search,
  LayoutDashboard,
  Server,
  MessageSquare,
  Headphones,
  Users,
  Contact,
  MessageCircle,
  Phone,
  Voicemail,
  Bell,
  UserCircle,
  Layers,
  Inbox,
  BarChart3,
  FlaskConical,
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore.js'
import { useDebouncedValue } from '../../hooks/useDebouncedValue.js'
import { useLauncherCounts } from '../../hooks/useLauncherCounts.js'
import { searchGlobal, type SearchResults } from '../../api/search.js'
import { getChannelConfig } from '../../modules/conversations/channelConfig.js'
import voz360Logo from '../../assets/voz360.png'

const ICON_CLS = 'h-[18px] w-[18px]'

interface PageShortcut {
  path: string
  label: string
  icon: JSX.Element
  /** Section heading the row sits under when the search box is empty. */
  section: string
  /** Optional permission key gating visibility. */
  permission?: string
  /** Shown as a launcher row when the box is empty. Others are search-only. */
  primary?: boolean
}

// The launcher catalog. Kept in sync with AgentLayout's ROUTE_TAB_MAP so an
// opened page shows the matching tab label/icon. Order matters: rows render in
// this order and keyboard navigation follows the same sequence. Only `primary`
// pages render on the empty page; the rest stay reachable by typing.
const PAGE_SHORTCUTS: readonly PageShortcut[] = [
  { path: '/agent', label: 'My Dashboard', icon: <LayoutDashboard className={ICON_CLS} />, section: 'Workspace', primary: true },
  { path: '/agent/conversation/inbox', label: 'Inbox', icon: <Inbox className={ICON_CLS} />, section: 'Workspace', primary: true },
  { path: '/agent/conversation/omnichannel-queue', label: 'Omnichannel Queue', icon: <Layers className={ICON_CLS} />, section: 'Workspace', primary: true },
  { path: '/agent/company-directory', label: 'Company Directory', icon: <Users className={ICON_CLS} />, section: 'People & comms', primary: true },
  { path: '/agent/chats', label: 'Chats', icon: <MessageCircle className={ICON_CLS} />, section: 'People & comms', primary: true },
  { path: '/agent/calls', label: 'Recent Calls', icon: <Phone className={ICON_CLS} />, section: 'People & comms', primary: true },
  { path: '/agent/dashboard/pbx', label: 'PBX Dashboard', icon: <Server className={ICON_CLS} />, section: 'Dashboards' },
  { path: '/agent/dashboard/conversations', label: 'Conversation Dashboard', icon: <MessageSquare className={ICON_CLS} />, section: 'Dashboards' },
  { path: '/agent/dashboard/call-center', label: 'Call Center Dashboard', icon: <Headphones className={ICON_CLS} />, section: 'Dashboards' },
  { path: '/agent/real-time-analytics/live-operations', label: 'Campaign Summary', icon: <BarChart3 className={ICON_CLS} />, section: 'Dashboards', permission: 'supervisor:campaign_summary' },
  { path: '/agent/real-time-analytics/campaign-summary', label: 'Campaign Summary (Demo)', icon: <FlaskConical className={ICON_CLS} />, section: 'Dashboards', permission: 'supervisor:campaign_summary' },
  { path: '/agent/conversation/search', label: 'Search Conversation', icon: <Search className={ICON_CLS} />, section: 'More' },
  { path: '/agent/clients', label: 'Clients', icon: <Contact className={ICON_CLS} />, section: 'More', permission: 'clients:view' },
  { path: '/agent/voicemail', label: 'Voicemail', icon: <Voicemail className={ICON_CLS} />, section: 'More', permission: 'voicemail:view_own' },
  { path: '/agent/notifications', label: 'Notifications', icon: <Bell className={ICON_CLS} />, section: 'More' },
  { path: '/agent/reports', label: 'Reports', icon: <BarChart3 className={ICON_CLS} />, section: 'More' },
  // Devices is intentionally disabled — re-add with a `Settings` icon import.
  { path: '/agent/profile', label: 'Profile', icon: <UserCircle className={ICON_CLS} />, section: 'More' },
]

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 250

interface InteractionRow {
  key: string
  href: string
  primary: string
  secondary?: string
  group: string
  icon: JSX.Element
}

/** Flattens /search results into openable agent-scoped rows. */
function toInteractionRows(results: SearchResults | null): InteractionRow[] {
  if (!results) return []
  const rows: InteractionRow[] = []

  for (const c of results.conversations) {
    const cfg = getChannelConfig(c.channelOrigin)
    const Icon = cfg.icon
    rows.push({
      key: `conversation:${c.id}`,
      href: `/agent/conversation/inbox?conversationId=${c.id}`,
      primary: c.subject?.trim() ? c.subject : c.referenceId,
      secondary: `${c.referenceId} · ${cfg.label}`,
      group: 'Conversations',
      icon: <Icon className="h-4 w-4" />,
    })
  }
  for (const m of results.messages) {
    rows.push({
      key: `message:${m.id}`,
      href: `/agent/conversation/inbox?conversationId=${m.conversationId}`,
      primary: m.contentPreview,
      secondary: `${m.direction} · ${m.sentAt}`,
      group: 'Messages',
      icon: <MessageSquare className="h-4 w-4" />,
    })
  }
  for (const ct of results.contacts) {
    rows.push({
      key: `contact:${ct.id}`,
      href: '/agent/company-directory',
      primary: ct.name,
      secondary: ct.email ? `${ct.phoneE164} · ${ct.email}` : ct.phoneE164,
      group: 'Contacts',
      icon: <Contact className="h-4 w-4" />,
    })
  }
  for (const call of results.calls) {
    rows.push({
      key: `call:${call.id}`,
      href: '/agent/calls',
      primary: `${call.fromNumber} → ${call.toNumber}`,
      secondary: call.startTime,
      group: 'Calls',
      icon: <Phone className="h-4 w-4" />,
    })
  }
  for (const a of results.agents) {
    rows.push({
      key: `agent:${a.id}`,
      href: '/agent/company-directory',
      primary: a.userFullName ?? `Extension ${a.extension}`,
      secondary: `ext ${a.extension}`,
      group: 'Agents',
      icon: <Users className="h-4 w-4" />,
    })
  }

  return rows
}

export default function NewTabPage(): JSX.Element {
  const navigate = useNavigate()
  const permissions = useAuthStore((s) => s.user?.permissions ?? [])
  const userId = useAuthStore((s) => s.user?.sub) ?? ''
  const counts = useLauncherCounts(userId)
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<SearchResults | null>(null)
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS)

  // Autofocus the search box the moment the tab opens (Chrome-like).
  useEffect(() => {
    const id = window.setTimeout(() => inputRef.current?.focus(), 0)
    return () => window.clearTimeout(id)
  }, [])

  // Permission-gated launcher list.
  const visiblePages = useMemo(
    () => PAGE_SHORTCUTS.filter((p) => !p.permission || permissions.includes(p.permission)),
    [permissions],
  )

  const trimmed = query.trim()
  const searching = trimmed.length > 0

  // Pages whose label matches the query.
  const matchedPages = useMemo(() => {
    if (!searching) return visiblePages
    const q = trimmed.toLowerCase()
    return visiblePages.filter((p) => p.label.toLowerCase().includes(q))
  }, [visiblePages, searching, trimmed])

  // Empty box → only the curated primary rows. Searching → every match.
  const displayedPages = useMemo(
    () => (searching ? matchedPages : visiblePages.filter((p) => p.primary)),
    [searching, matchedPages, visiblePages],
  )

  // Grouped for rendering. While searching everything collapses into a single
  // "Pages" section; otherwise rows sit under their catalog section. Source
  // order is preserved so `flatIndex` lines up with keyboard navigation.
  const pageSections = useMemo(() => {
    const sections: { section: string; rows: { page: PageShortcut; flatIndex: number }[] }[] = []
    displayedPages.forEach((page, i) => {
      const section = searching ? 'Pages' : page.section
      const last = sections[sections.length - 1]
      if (!last || last.section !== section) {
        sections.push({ section, rows: [{ page, flatIndex: i }] })
      } else {
        last.rows.push({ page, flatIndex: i })
      }
    })
    return sections
  }, [displayedPages, searching])

  // Fire /search once the debounced query passes the min length.
  useEffect(() => {
    if (debouncedQuery.trim().length < MIN_QUERY_LENGTH) {
      setResults(null)
      setActiveIndex(0)
      return
    }
    let cancelled = false
    void searchGlobal(debouncedQuery.trim())
      .then((res) => {
        if (!cancelled) {
          setResults(res)
          setActiveIndex(0)
        }
      })
      .catch(() => {
        if (!cancelled) setResults(null)
      })
    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  const interactions = useMemo(() => toInteractionRows(results), [results])

  // Combined keyboard-navigation order: matched pages first, then interactions.
  const flatHrefs = useMemo(
    () => [...displayedPages.map((p) => p.path), ...interactions.map((r) => r.href)],
    [displayedPages, interactions],
  )

  const open = useCallback((href: string) => navigate(href), [navigate])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (flatHrefs.length === 0) return
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setActiveIndex((i) => Math.min(i + 1, flatHrefs.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setActiveIndex((i) => Math.max(i - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const href = flatHrefs[activeIndex] ?? flatHrefs[0]
        if (href) open(href)
      }
    },
    [flatHrefs, activeIndex, open],
  )

  // Group interactions by section for rendering, tracking flat index (offset
  // by the number of matched pages) so the highlight lines up with keyboard nav.
  const interactionGroups = useMemo(() => {
    const groups: { group: string; rows: { row: InteractionRow; flatIndex: number }[] }[] = []
    interactions.forEach((row, i) => {
      const flatIndex = displayedPages.length + i
      const last = groups[groups.length - 1]
      if (!last || last.group !== row.group) {
        groups.push({ group: row.group, rows: [{ row, flatIndex }] })
      } else {
        last.rows.push({ row, flatIndex })
      }
    })
    return groups
  }, [interactions, displayedPages.length])

  // Long result sets can run past the fold; keep the ↑/↓ target on screen.
  useEffect(() => {
    const active = listRef.current?.querySelector('[data-active="true"]')
    // Guarded: jsdom (and older embedded webviews) don't implement it.
    if (active instanceof HTMLElement && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest' })
    }
  }, [activeIndex])

  const showEmpty =
    searching &&
    displayedPages.length === 0 &&
    interactions.length === 0 &&
    results !== null

  const hasRows = displayedPages.length > 0 || interactionGroups.length > 0

  return (
    <div className="mx-auto flex min-h-full w-full max-w-xl flex-col px-6 pb-16 pt-[10vh]">
      {/* Brand wordmark — sits above the search box. */}
      <img src={voz360Logo} alt="Voz360" className="mb-7 h-9 select-none self-center" draggable={false} />

      {/* Search box */}
      <div className="w-full">
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-all hover:shadow-md focus-within:border-[#3366FF] focus-within:shadow-md">
          <Search className="h-[18px] w-[18px] shrink-0 text-slate-400" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search pages, conversations, contacts, calls…"
            aria-label="Search"
            className="w-full bg-transparent text-[15px] text-slate-800 placeholder:text-slate-400 outline-none"
          />
        </div>
        {searching && trimmed.length < MIN_QUERY_LENGTH && (
          <p className="mt-2 px-4 text-xs text-slate-400">
            Type at least {MIN_QUERY_LENGTH} characters to search interactions.
          </p>
        )}
      </div>

      {/* The palette itself — one vertical list of rows, grouped by section.
          Pages first, then interactions, in the same order as `flatHrefs` so
          the ↑/↓ highlight always lands on the row it looks like it's on. */}
      <div ref={listRef} className="mt-6 w-full">
        {pageSections.map((s) => (
          <SectionList key={s.section} title={s.section}>
            {s.rows.map(({ page, flatIndex }) => (
              <PaletteRow
                key={page.path}
                icon={page.icon}
                title={page.label}
                badge={counts[page.path]}
                isActive={flatIndex === activeIndex}
                onActivate={() => open(page.path)}
                onHover={() => setActiveIndex(flatIndex)}
              />
            ))}
          </SectionList>
        ))}

        {interactionGroups.map((g) => (
          <SectionList key={g.group} title={g.group}>
            {g.rows.map(({ row, flatIndex }) => (
              <PaletteRow
                key={row.key}
                icon={row.icon}
                title={row.primary}
                subtitle={row.secondary}
                isActive={flatIndex === activeIndex}
                onActivate={() => open(row.href)}
                onHover={() => setActiveIndex(flatIndex)}
              />
            ))}
          </SectionList>
        ))}
      </div>

      {showEmpty && (
        <div className="mt-12 text-center text-sm text-slate-400">
          No pages or interactions match “{trimmed}”.
        </div>
      )}

      {/* Keyboard legend — the palette is keyboard-first, so say so. */}
      {hasRows && (
        <div className="mt-6 flex items-center justify-center gap-4 text-[11px] text-slate-400">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            navigate
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd>
            open
          </span>
        </div>
      )}
    </div>
  )
}

/** Small keycap used by the row hint and the keyboard legend. */
function Kbd({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <kbd className="inline-flex h-[18px] min-w-[18px] items-center justify-center rounded border border-slate-200 bg-white px-1 font-sans text-[10px] leading-none text-slate-400">
      {children}
    </kbd>
  )
}

function SectionList({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="mb-4">
      <div className="px-3 pb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">
        {title}
      </div>
      <ul>{children}</ul>
    </div>
  )
}

interface PaletteRowProps {
  icon: JSX.Element
  title: string
  subtitle?: string | undefined
  /** Live count; rendered only when > 0. */
  badge?: number | undefined
  isActive: boolean
  onActivate: () => void
  onHover: () => void
}

/**
 * One palette row: accent bar, icon, label (+ optional subtitle), optional
 * live badge, and an ⏎ hint on the active row. `onMouseDown` (not `onClick`)
 * opens it so the row still works while the search input holds focus.
 */
function PaletteRow({ icon, title, subtitle, badge, isActive, onActivate, onHover }: PaletteRowProps): JSX.Element {
  return (
    <li>
      <button
        type="button"
        data-active={isActive || undefined}
        onMouseEnter={onHover}
        onMouseDown={(e) => {
          e.preventDefault()
          onActivate()
        }}
        className={`flex w-full items-center gap-3 rounded-xl py-2 pl-1 pr-3 text-left transition-colors ${
          isActive ? 'bg-[#3366FF]/[0.06]' : 'hover:bg-slate-100/70'
        }`}
      >
        <span
          aria-hidden
          className={`h-5 w-[3px] shrink-0 rounded-full transition-colors ${
            isActive ? 'bg-[#3366FF]' : 'bg-transparent'
          }`}
        />
        <span
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition-colors ${
            isActive ? 'bg-[#3366FF]/10 text-[#3366FF]' : 'bg-slate-100 text-slate-500'
          }`}
        >
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-sm font-medium ${isActive ? 'text-[#3366FF]' : 'text-slate-700'}`}
          >
            {title}
          </span>
          {subtitle && <span className="block truncate text-xs text-slate-500">{subtitle}</span>}
        </span>
        {badge !== undefined && badge > 0 && (
          <span className="shrink-0 rounded-full bg-[#3366FF]/10 px-2 py-0.5 text-[11px] font-semibold tabular-nums text-[#3366FF]">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
        {/* Placeholder keeps the row width stable as the hint appears/disappears. */}
        {isActive ? <Kbd>↵</Kbd> : <span aria-hidden className="h-[18px] w-[18px] shrink-0" />}
      </button>
    </li>
  )
}
